/**
 * PhotoUploadDialog — Upload / replace resident profile photo.
 *
 * Flow:
 *   1. User selects an image file (accept image/*)
 *   2. Preview is shown
 *   3. User clicks Upload
 *   4. onUpload(base64Data, fileExtension) is called
 */

import { useState, useRef, useCallback } from 'react';

const PhotoUploadDialog = ({ onClose, onUpload, saving, currentImage }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [fileExt, setFileExt] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Get file extension
    const nameParts = file.name.split('.');
    const ext = nameParts.length > 1 ? nameParts.pop().toLowerCase() : 'png';
    setFileExt(ext);

    // Create preview
    const previewURL = URL.createObjectURL(file);
    setPreviewUrl(previewURL);

    // Read as base64
    const reader = new FileReader();
    reader.onload = (evt) => {
      // evt.target.result is a data URL like "data:image/png;base64,..."
      // We want just the base64 part
      const base64 = evt.target.result.split(',')[1];
      setFileData(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleUpload = useCallback(() => {
    if (!fileData || !fileExt) return;
    onUpload(fileData, fileExt);
  }, [fileData, fileExt, onUpload]);

  return (
    <div className="pp-dialog-backdrop" onClick={onClose}>
      <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pp-dialog__header">
          <h2 className="pp-dialog__title">Upload Photo</h2>
          <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
        </div>
        <div className="pp-dialog__body">
          {/* File Input */}
          <label className="rpp-file-input-label">
            <span>Choose an image file&hellip;</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="rpp-file-input-hidden"
              onChange={handleFileChange}
            />
          </label>

          {/* Preview */}
          {previewUrl ? (
            <div className="rpp-photo-preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          ) : null}
        </div>
        <div className="pp-dialog__footer">
          <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="dp-btn dp-btn--primary"
            onClick={handleUpload}
            disabled={saving || !fileData}
          >
            {saving ? 'Uploading\u2026' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadDialog;
